import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Collapse,
  LinearProgress,
  Typography,
  Alert,
  AlertTitle,
  List,
} from "@mui/material";
import axios from "axios";
import {
  OcCard,
  OcCardButtonBar,
  OcDropzoneArea,
  OcPageTransition,
  OcFileListItem,
  type FileRejection,
} from "oc/components";
import {
  useOcApi,
  useOcIsMobile,
  useOcSnackbar,
  useOcCurrentUserState,
} from "oc/hooks";

type MassUploadFilesProps = {
  setShowNewCard: (show: boolean) => void;
  onUploadComplete?: () => void;
};

type FileValidation = {
  file: File;
  isValid: boolean;
  error?: string;
  identifier?: string;
  description?: string;
};

type UploadResult = {
  filename: string;
  identifier: string;
  employee?: string;
  contractCode?: string;
  _id?: string;
  error?: string;
};

type MassUploadResponse = {
  successful: UploadResult[];
  failed: UploadResult[];
};

const parseFilename = (filename: string): { success: boolean; id?: string; description?: string; error?: string } => {
  if (!filename || typeof filename !== 'string') {
    return { success: false, error: 'INVALID_FILENAME' };
  }

  const nameWithoutExt = filename.replace(/\.[^.]+$/, '');

  const pattern = /^([A-Z0-9]+)_(.*)$/i;
  const match = nameWithoutExt.match(pattern);

  if (!match) {
    return { success: false, error: 'INVALID_FILENAME_FORMAT' };
  }

  const id = match[1].toUpperCase();
  const description = match[2].trim();

  if (id.length !== 5) {
    return { success: false, error: 'IDENTIFIER_INVALID_LENGTH' };
  }

  return {
    success: true,
    id,
    description: description || ''
  };
};

const isValidExtension = (filename: string): boolean => {
  const supportedExtensions = [
    '.pdf', '.doc', '.docx', '.xls', '.xlsx',
    '.png', '.jpg', '.jpeg', '.gif'
  ];

  const ext = filename.match(/\.[^.]+$/)?.[0]?.toLowerCase();
  return ext ? supportedExtensions.includes(ext) : false;
};

export const MyDocumentsMassUploadFiles = ({
  setShowNewCard,
  onUploadComplete,
}: MassUploadFilesProps) => {
  const { t } = useTranslation();
  const isMobile = useOcIsMobile();

  const [files, setFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [failedFiles, setFailedFiles] = useState<{ filename: string; error: string }[]>([]);
  const { setOpenMessage } = useOcSnackbar();
  const { employeeCode: uploaderEmployeeCode } = useOcCurrentUserState();

  const fileValidations = useMemo((): FileValidation[] => {
    const validations = files.map((file) => {
      if (file.size > 9437184) {
        return {
          file,
          isValid: false,
          error: 'FILE_TOO_LARGE',
        };
      }

      if (!isValidExtension(file.name)) {
        return {
          file,
          isValid: false,
          error: 'UNSUPPORTED_FILE_TYPE',
        };
      }

      const parsed = parseFilename(file.name);
      if (!parsed.success) {
        return {
          file,
          isValid: false,
          error: parsed.error,
        };
      }

      return {
        file,
        isValid: true,
        identifier: parsed.id,
        description: parsed.description,
      };
    });

    // Add failed uploads from server as invalid files (only those with error messages)
    const failedValidations = failedFiles
      .filter(failed => failed.error) // Only include files with error messages
      .map(failed => {
        const dummyFile = new File([], failed.filename, { type: 'application/pdf' });
        Object.defineProperty(dummyFile, 'name', {
          value: failed.filename,
          writable: false,
        });
        return {
          file: dummyFile,
          isValid: false,
          error: failed.error,
        };
      });

    return [...validations, ...failedValidations];
  }, [files, failedFiles]);

  const validFiles = fileValidations.filter((v) => v.isValid);

  // Files to display in the "Selected files" section (excludes files that are in failedFiles list)
  // Pre-upload validation errors are shown here with red background, post-upload errors in separate section
  const filesToDisplay = fileValidations.filter(v => v.isValid || !failedFiles.some(f => f.filename === v.file.name));

  // Only show "Selected files" section if there's at least one valid file
  const hasValidFiles = filesToDisplay.some(v => v.isValid);

  const handleDropzoneChange = (newFiles: File | File[]) => {
    const fileArray = Array.isArray(newFiles) ? newFiles : [newFiles];
    setFiles(prev => {
      const totalFiles = prev.length + fileArray.length;
      if (totalFiles > 50) {
        setOpenMessage({
          type: "error",
          message: t("MY_DOCUMENTS_MASS_TOO_MANY_FILES"),
        });
        // Only add files up to the limit
        const remainingSlots = 50 - prev.length;
        return remainingSlots > 0 ? [...prev, ...fileArray.slice(0, remainingSlots)] : prev;
      }
      return [...prev, ...fileArray];
    });
  };

  const handleDropRejected = (fileRejections: FileRejection[]) => {
    // Check first rejection's error code to show appropriate message
    const errorCode = fileRejections[0]?.errors[0]?.code;

    let message;
    if (errorCode === 'file-too-large') {
      message = t("OC_FILE_UPLOAD_SIZE_FAILED");
    } else if (errorCode === 'too-many-files') {
      message = t("MY_DOCUMENTS_MASS_TOO_MANY_FILES");
    } else if (errorCode === 'file-invalid-type') {
      message = t("MY_DOCUMENTS_ERROR_UNSUPPORTED_TYPE");
    } else {
      message = t("MY_DOCUMENTS_UNKNOWN_ERROR");
    }

    setOpenMessage({
      type: "error",
      message,
    });
  };

  const { apiUrl } = useOcApi();
  const url = `${apiUrl}/documents/createMass`;

  const handleUpload = async () => {
    if (validFiles.length === 0) {
      setOpenMessage({
        type: "error",
        message: t("MY_DOCUMENTS_MASS_NO_VALID_FILES"),
      });
      return;
    }

    const formData = new FormData();

    // Generate unique ID for this mass upload batch
    const massUploadId = crypto.randomUUID();

    formData.append("uploaderEmployeeCode", uploaderEmployeeCode);
    formData.append("massUploadId", massUploadId);

    validFiles.forEach((validation) => {
      formData.append("files", validation.file);
    });

    setUploading(true);
    setUploadProgress(0);

    try {
      const config = {
        timeout: 300000, // 5 minutes timeout for mass upload
        onUploadProgress: (progressEvent: import("axios").AxiosProgressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded! * 100) / (progressEvent.total || 1),
          );
          setUploadProgress(percentCompleted);
        },
      };

      const response = await axios.post<UploadResult[]>(url, formData, config);

      const results: MassUploadResponse = {
        successful: response.data.filter(r => !r.error),
        failed: response.data.filter(r => r.error),
      };

      const successCount = results.successful.length;
      const failedCount = results.failed.length;

      // Remove all uploaded files (both successful and failed) from the list
      const allUploadedFilenames = new Set([
        ...results.successful.map(r => r.filename),
        ...results.failed.map(r => r.filename)
      ]);
      setFiles(prev => prev.filter(file => !allUploadedFilenames.has(file.name)));

      // Store failed files to display with error messages
      if (failedCount > 0) {
        setFailedFiles(results.failed.map(f => ({
          filename: f.filename,
          error: f.error || "UPLOAD_FAILED"
        })));
      } else {
        setFailedFiles([]);
      }

      if (failedCount === 0) {
        setOpenMessage({
          type: "success",
          message: t("MY_DOCUMENTS_MASS_UPLOAD_ALL_SUCCESS", { count: successCount }),
        });
        setShowNewCard(false);
      } else if (successCount === 0) {
        setOpenMessage({
          type: "error",
          message: t("MY_DOCUMENTS_MASS_UPLOAD_ALL_FAILED", { count: failedCount }),
        });
      } else {
        setOpenMessage({
          type: "warning",
          message: t("MY_DOCUMENTS_MASS_UPLOAD_PARTIAL", {
            success: successCount,
            failed: failedCount
          }),
        });
      }

      // Refresh mass upload history if any files were uploaded successfully
      if (successCount > 0 && onUploadComplete) {
        onUploadComplete();
      }
    } catch (error) {
      setOpenMessage({
        type: "error",
        message: t("MY_DOCUMENTS_MASS_UPLOAD_ERROR"),
      });
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (index: number) => {
    const validation = fileValidations[index];
    const filename = validation.file.name;

    // Remove from files list
    setFiles((prev) => prev.filter((f) => f.name !== filename));

    // Remove from failed files list if present
    setFailedFiles((prev) => prev.filter((f) => f.filename !== filename));
  };

  const removeAllFiles = () => {
    setFiles([]);
    setFailedFiles([]);
  };

  const handleCancel = () => {
    setFiles([]);
    setShowNewCard(false);
  };

  const getErrorMessage = (errorCode?: string): string => {
    if (!errorCode) return t("MY_DOCUMENTS_UNKNOWN_ERROR");

    const errorMap: Record<string, string> = {
      'INVALID_FILENAME_FORMAT': t("MY_DOCUMENTS_ERROR_INVALID_FORMAT"),
      'IDENTIFIER_INVALID_LENGTH': t("MY_DOCUMENTS_ERROR_INVALID_LENGTH"),
      'IDENTIFIER_NOT_FOUND': t("MY_DOCUMENTS_ERROR_ID_NOT_FOUND"),
      'FILE_TOO_LARGE': t("OC_FILE_UPLOAD_SIZE_FAILED"),
      'UNSUPPORTED_FILE_TYPE': t("MY_DOCUMENTS_ERROR_UNSUPPORTED_TYPE"),
    };

    return errorMap[errorCode] || errorCode;
  };

  return (
    <OcPageTransition>
      <OcCard label={t("MY_DOCUMENTS_MASS_UPLOAD_TITLE")}>
        <Box height="16px" />
        {files.length === 0 && (
          <Box mb={2}>
            <Alert severity="info">
              <AlertTitle>{t("MY_DOCUMENTS_MASS_INSTRUCTIONS_TITLE")}</AlertTitle>
              <Box component="span" sx={{ whiteSpace: 'pre-line' }}>
                {t("MY_DOCUMENTS_MASS_INSTRUCTIONS_TEXT")}
              </Box>
              <br />
              <strong>{t("MY_DOCUMENTS_MASS_FILENAME_FORMAT")}</strong>
            </Alert>
          </Box>
        )}
        {!uploading && (
          <Box
            display="flex"
            justifyContent="center"
            flexDirection="column"
            alignItems="center"
          >
            <Box width={isMobile ? "100%" : "100%"}>
              <Collapse in={true} timeout="auto" id="dropzone-area">
                <OcDropzoneArea
                  dropzoneText={t("MY_DOCUMENTS_MASS_DROP_TEXT")}
                  acceptedFiles={{
                    "application/*": [".pdf", ".doc", ".docx", ".xls", ".xlsx"],
                    "image/*": [".png", ".gif", ".jpeg", ".jpg"],
                  }}
                  onChange={handleDropzoneChange}
                  onDropRejected={handleDropRejected}
                  height="120px"
                  maxFiles={50}
                  maxSize={9437184}
                />
              </Collapse>
            </Box>
          </Box>
        )}
        <Box height="16px" />
        {filesToDisplay.length > 0 && (
          <>
            {hasValidFiles && (
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="body1">
                  {t("MY_DOCUMENTS_SELECTED_FILES")} ({filesToDisplay.filter(v => v.isValid).length})
                </Typography>
                <Button
                  variant="text"
                  color="secondary"
                  onClick={removeAllFiles}
                  id="remove-all-files"
                >
                  {t("MY_DOCUMENTS_REMOVE_ALL")}
                </Button>
              </Box>
            )}
            <List disablePadding={!hasValidFiles}>
              {filesToDisplay.map((validation, index) => (
                <OcFileListItem
                  key={index}
                  filename={validation.file.name}
                  error={validation.isValid ? undefined : getErrorMessage(validation.error)}
                  onDelete={() => removeFile(index)}
                  chip={validation.isValid && validation.identifier ? validation.identifier : undefined}
                  isMobile={isMobile}
                  uploadFailed={!validation.isValid}
                  id={`doc-${index + 1}`}
                />
              ))}
            </List>
          </>
        )}
        {failedFiles.length > 0 && (
          <List disablePadding>
            {failedFiles.map((failed, index) => (
              <OcFileListItem
                key={`failed-${index}`}
                filename={failed.filename}
                error={getErrorMessage(failed.error)}
                onDelete={() => {
                  setFailedFiles(prev => prev.filter((_, i) => i !== index));
                }}
                chip={undefined}
                isMobile={isMobile}
                uploadFailed={true}
                id={`doc-failed-${index + 1}`}
              />
            ))}
          </List>
        )}
        {uploading && (
          <Box>
            <Typography variant="body1" align="center" mb={2}>
              {t("MY_DOCUMENTS_UPLOADING_FILES")} ({uploadProgress}%)
            </Typography>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </Box>
        )}
        <OcCardButtonBar
          handleRightButton={handleCancel}
          rightButtonColor="secondary"
          rightButtonText={t("OC_CANCEL")}
          handlePrimaryButton={handleUpload}
          primaryButtonText={t("MY_DOCUMENTS_UPLOAD_FILES")}
          primaryButtonId="upload-files-button"
          primaryButtonDisabled={validFiles.length === 0 || uploading}
        />
        <Box height="16px" />
      </OcCard>
    </OcPageTransition>
  );
};

export default MyDocumentsMassUploadFiles;
